import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { KeyboardEvent } from 'react'
import { useDebounce } from '../../../hooks/useDebounce'

export const SEARCH_DEBOUNCE_MS = 300

/**
 * Above this many results we refuse to page through the whole list for "Alle auswählen".
 * The API caps a page at 100–500 rows, so a bigger result set means a long burst of requests.
 */
export const MAX_BULK_AUTOLOAD = 500

/**
 * `id` may arrive as a numeric string (e.g. `User.id` in the contract) while the selection is
 * always `number[]` — hence the coercion through `toId` everywhere below.
 */
export interface BaseEntity {
  id: number | string
}

export interface InfiniteHookResult<T> {
  data?: {
    items: T[]
    meta?: { page: number; limit: number; total: number }
  }
  fetchNextPage: () => unknown
  hasNextPage: boolean
  isFetchingNextPage: boolean
  isLoading: boolean
  isError?: boolean
  refetch?: () => unknown
}

/**
 * Params stay `any` on purpose: every entity hook takes its own filter type, and a narrower
 * signature here would break the call sites under `strictFunctionTypes`. The *result* is typed.
 */
export type UseInfiniteHook<T> = (params: any) => InfiniteHookResult<T>

/** "loaded" = the auto-load was skipped because the result set exceeds MAX_BULK_AUTOLOAD. */
type BulkState = 'idle' | 'loading' | 'loaded-only'

const EMPTY: never[] = []

const toId = (id: number | string) => Number(id)

interface Params<T extends BaseEntity> {
  value: number[]
  onChange: (ids: number[]) => void
  useInfiniteHook: UseInfiniteHook<T>
  searchParamName: string
  limit: number
  query?: unknown
  disabled?: boolean
  markedIds: number[]
  onMarkedChange?: (ids: number[]) => void
}

export function useEntityMultiSelect<T extends BaseEntity>({
  value,
  onChange,
  useInfiniteHook,
  searchParamName,
  limit,
  query,
  disabled,
  markedIds,
  onMarkedChange,
}: Params<T>) {
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS)

  const listRef = useRef<HTMLDivElement>(null)
  const sentinelRef = useRef<HTMLDivElement>(null)

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    refetch,
  } = useInfiniteHook({
    limit,
    ...(query as object),
    [searchParamName]: debouncedSearch || undefined,
  })

  const items = data?.items ?? (EMPTY as T[])
  const total = data?.meta?.total

  const selectedSet = useMemo(() => new Set(value.map(toId)), [value])
  const markedSet = useMemo(() => new Set(markedIds.map(toId)), [markedIds])

  /**
   * Identity of the current *result set*. Everything that changes which rows the server returns
   * belongs in here — and nothing that merely changes the selection does.
   */
  const orderKey = useMemo(
    () => JSON.stringify([debouncedSearch, query ?? null, limit, searchParamName]),
    [debouncedSearch, query, limit, searchParamName],
  )

  // Read only when the order is rebuilt, so ticking a row can never reshuffle the list.
  const selectedSnapshotRef = useRef(selectedSet)
  selectedSnapshotRef.current = selectedSet

  const orderRef = useRef<{ key: string; ids: number[] }>({ key: '', ids: [] })

  /**
   * Selected-first ordering is a snapshot, not a live sort:
   * - result set changed (new search/filters) → rebuild, selected rows on top;
   * - next page appended → keep the established order and append the newcomers;
   * - selection changed → nothing moves.
   */
  const orderedItems = useMemo(() => {
    // Never claim an order for a result set that has not arrived yet. Committing `orderKey` while
    // the first page is still in flight would push the arriving page into the "append" branch
    // below, and the selected-first rebuild would never run.
    if (!items.length) return EMPTY as T[]

    const byId = new Map<number, T>()
    items.forEach((item) => byId.set(toId(item.id), item))

    const prev = orderRef.current
    let ids: number[]

    if (prev.key !== orderKey) {
      const snapshot = selectedSnapshotRef.current
      const all = Array.from(byId.keys())
      ids = [
        ...all.filter((id) => snapshot.has(id)),
        ...all.filter((id) => !snapshot.has(id)),
      ]
    } else {
      const kept = prev.ids.filter((id) => byId.has(id))
      const keptSet = new Set(kept)
      ids = [...kept, ...Array.from(byId.keys()).filter((id) => !keptSet.has(id))]
    }

    orderRef.current = { key: orderKey, ids }

    return ids.map((id) => byId.get(id) as T)
  }, [items, orderKey])

  // Keep the previous rows on screen while a new search resolves — the box never flashes empty.
  const lastGoodRef = useRef<T[]>(EMPTY as T[])
  useEffect(() => {
    if (!isLoading) lastGoodRef.current = orderedItems
  }, [isLoading, orderedItems])

  const isStale = isLoading && lastGoodRef.current.length > 0
  const isTyping = search !== debouncedSearch

  const [bulk, setBulk] = useState<BulkState>('idle')

  const visibleItems = isStale ? lastGoodRef.current : orderedItems

  // A new result set invalidates any running bulk load.
  useEffect(() => {
    setBulk('idle')
  }, [orderKey])

  /**
   * Ids in `value` that no row can represent. Only meaningful once the loaded set IS the whole
   * list (no search narrowing it, no page left to fetch) — otherwise a selection on an unfetched
   * page would look like a phantom. Explains a count that no visible row accounts for.
   */
  const loadedIds = useMemo(() => new Set(items.map((item) => toId(item.id))), [items])
  const missingSelectedCount = useMemo(
    () => value.reduce((acc, id) => (loadedIds.has(toId(id)) ? acc : acc + 1), 0),
    [value, loadedIds],
  )
  const showMissingHint =
    !isLoading && !hasNextPage && !search && missingSelectedCount > 0

  const toggle = useCallback(
    (id: number) => {
      if (disabled) return

      const numId = toId(id)
      const next = selectedSet.has(numId)
        ? value.map(toId).filter((x) => x !== numId)
        : [...value.map(toId), numId]

      onChange(next)
    },
    [disabled, selectedSet, value, onChange],
  )

  const toggleMark = useCallback(
    (id: number) => {
      if (disabled || !onMarkedChange) return

      const numId = toId(id)
      const isMarked = markedSet.has(numId)

      onMarkedChange(
        isMarked
          ? markedIds.map(toId).filter((x) => x !== numId)
          : [...markedIds.map(toId), numId],
      )

      if (!isMarked && !selectedSet.has(numId)) {
        onChange([...value.map(toId), numId])
      }
    },
    [disabled, onMarkedChange, markedSet, markedIds, selectedSet, value, onChange],
  )

  // ── Bulk selection ────────────────────────────────────────────────────────────
  // The auto-load effect must always see the freshest page, not the closure it started with.
  const latestRef = useRef({ items, value, onChange })
  latestRef.current = { items, value, onChange }

  const commitLoaded = useCallback(() => {
    const { items: current, value: currentValue, onChange: emit } = latestRef.current
    const next = new Set(currentValue.map(toId))
    current.forEach((item) => next.add(toId(item.id)))
    emit(Array.from(next))
  }, [])

  const selectAll = useCallback(() => {
    if (disabled || bulk === 'loading') return

    if (!hasNextPage) {
      commitLoaded()
      setBulk('idle')
      return
    }

    if (total !== undefined && total > MAX_BULK_AUTOLOAD) {
      commitLoaded()
      setBulk('loaded-only')
      return
    }

    setBulk('loading')
  }, [disabled, bulk, hasNextPage, total, commitLoaded])

  useEffect(() => {
    if (bulk !== 'loading') return

    if (hasNextPage) {
      if (!isFetchingNextPage) fetchNextPage()
      return
    }

    commitLoaded()
    setBulk('idle')
  }, [bulk, hasNextPage, isFetchingNextPage, fetchNextPage, commitLoaded])

  const clearAll = useCallback(() => {
    if (disabled) return
    onChange([])
    setBulk('idle')
  }, [disabled, onChange])

  // ── Infinite scroll ───────────────────────────────────────────────────────────
  useEffect(() => {
    const root = listRef.current
    const sentinel = sentinelRef.current

    // No observer while a page is in flight — that is what keeps it from double-firing.
    if (!root || !sentinel || !hasNextPage || isFetchingNextPage) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) fetchNextPage()
      },
      { root, rootMargin: '160px' },
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, visibleItems.length])

  // ── Keyboard navigation ───────────────────────────────────────────────────────
  const [activeIndex, setActiveIndex] = useState(-1)
  const shouldFocusRef = useRef(false)

  useEffect(() => {
    if (!shouldFocusRef.current || activeIndex < 0) return
    shouldFocusRef.current = false

    const el = listRef.current?.querySelector<HTMLElement>(
      `[data-index="${activeIndex}"]`,
    )
    el?.focus({ preventScroll: true })
    el?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex, visibleItems])

  const focusIndex = useCallback(
    (index: number) => {
      const count = visibleItems.length
      if (!count) return
      shouldFocusRef.current = true
      setActiveIndex(Math.max(0, Math.min(count - 1, index)))
    },
    [visibleItems.length],
  )

  const onListKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      const count = visibleItems.length
      if (!count) return

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault()
          focusIndex(activeIndex + 1)
          break
        case 'ArrowUp':
          event.preventDefault()
          focusIndex(activeIndex - 1)
          break
        case 'Home':
          event.preventDefault()
          focusIndex(0)
          break
        case 'End':
          event.preventDefault()
          focusIndex(count - 1)
          break
        case ' ':
        case 'Enter': {
          const item = visibleItems[activeIndex]
          if (!item) return
          event.preventDefault()
          toggle(toId(item.id))
          break
        }
        case 'Escape':
          if (search) {
            event.preventDefault()
            setSearch('')
          }
          break
        default:
      }
    },
    [visibleItems, activeIndex, focusIndex, toggle, search],
  )

  const onSearchKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (event.key === 'ArrowDown' && visibleItems.length) {
        event.preventDefault()
        focusIndex(activeIndex < 0 ? 0 : activeIndex)
      }
      if (event.key === 'Escape' && search) {
        event.preventDefault()
        setSearch('')
      }
    },
    [visibleItems.length, activeIndex, focusIndex, search],
  )

  return {
    // search
    search,
    setSearch,
    isTyping,
    onSearchKeyDown,

    // data
    visibleItems,
    selectedSet,
    markedSet,
    total,
    count: value.length,

    // status
    isLoading,
    isStale,
    isError: Boolean(isError),
    isFetchingNextPage,
    hasNextPage,
    loadedCount: items.length,
    refetch,

    // actions
    toggle,
    toggleMark,
    selectAll,
    clearAll,
    isBulkLoading: bulk === 'loading',
    showLoadedOnlyHint: bulk === 'loaded-only',
    showMissingHint,
    missingSelectedCount,

    // a11y / refs
    listRef,
    sentinelRef,
    activeIndex,
    setActiveIndex,
    onListKeyDown,
  }
}
